import {
  DEFAULT_PAGE,
  DEFAULT_SIZE,
  DEFAULT_SORT,
  ordersToSortArr,
  PageResponse,
  PageSearchCondition,
} from "../../../types/page";
import {
  dataSearchTestResultItemList,
  getTestResultItemListAsync,
  setTestResultItemList,
} from "../../../store/dataSearchSlice";
import { useAppDispatch, useAppSelector, useListFilterValues } from "../../../hooks/hooks";
import { useEffect, useMemo, useState } from "react";
import { DataSearchListQueryType, DataSearchFilterValues } from "./useDataSearchFilterValues";
import { useUtil } from "../../../util/hooks/useUtil";
import { dateToString } from "../../../util/dateUtil";
import { useSortParam } from "../../../hooks/list/useSortParam";
import { DataSearchTestType, TestResultItemDataObj, TestResultSearchCondition } from "../../../types/dataSearch";
import { Gender } from "../../../types/common";

interface Args {
  onCompletedCallback?: () => void;
}

export function useTestResultItemList({ onCompletedCallback }: Args) {
  const dispatch = useAppDispatch();
  const testResultItemList = useAppSelector(dataSearchTestResultItemList);
  const { filterValues, searchList, listQueryType } = useListFilterValues();
  const dataSearchFilterValues = filterValues as DataSearchFilterValues;
  const { defaultErrorMessage } = useUtil();
  const { toSortArray } = useSortParam();

  const pageCondition = useMemo(
    () => ({
      page: testResultItemList?.number ?? DEFAULT_PAGE,
      size: testResultItemList?.size ?? DEFAULT_SIZE,
      sort: ordersToSortArr(testResultItemList?.orders),
    }),
    [testResultItemList]
  );

  const {
    qFilterValue,
    ageYearFilterValue,
    genderFilterValue,
    stateFilterValue,
    cityFilterValue,
    endedAtFromFilterValue,
    endedAtToFilterValue,
    testTypeFilterValue,
    onlyCompletedFilterValue,
    excludeTestAccountFilterValue,
  } = dataSearchFilterValues;

  const [totalCnt, setTotalCnt] = useState<number | null>(null);

  const getSearchParam = (): DataSearchListQueryType => ({
    q: qFilterValue.value ? qFilterValue.value.trim() : undefined,
    ageYear: ageYearFilterValue.value ? ageYearFilterValue.value : undefined,
    gender: genderFilterValue.value ? genderFilterValue.value : undefined,
    state: stateFilterValue.value ? stateFilterValue.value.trim() : undefined,
    city: cityFilterValue.value ? cityFilterValue.value.trim() : undefined,
    endedAtFrom: endedAtFromFilterValue.value ? dateToString(endedAtFromFilterValue.value) : undefined,
    endedAtTo: endedAtToFilterValue.value ? dateToString(endedAtToFilterValue.value) : undefined,
    testType: testTypeFilterValue.value ? testTypeFilterValue.value : undefined,
    onlyCompleted: onlyCompletedFilterValue.value ? "true" : undefined,
    excludeTestAccount: excludeTestAccountFilterValue.value ? "true" : undefined,
  });

  const searchTestResultItemList = (pageSearchCondition: PageSearchCondition) => {
    const param = getSearchParam();
    return () => searchList(pageSearchCondition, param)();
  };

  const getTestResultItemList = async (condition: TestResultSearchCondition, isFirstLoad?: boolean) => {
    try {
      const result: PageResponse<TestResultItemDataObj> = await dispatch(
        getTestResultItemListAsync(condition)
      ).unwrap();
      if (isFirstLoad) {
        setTotalCnt(result.totalElements);
      } else {
        dispatch(setTestResultItemList(result));
      }
      onCompletedCallback?.();
    } catch (e) {
      console.error(e);
      alert(defaultErrorMessage);
    }
  };

  useEffect(() => {
    getTestResultItemList({}, true).then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (listQueryType) {
      getTestResultItemList({
        pageCondition: {
          page: listQueryType.page ? Number(listQueryType.page) : DEFAULT_PAGE,
          size: listQueryType.size ? Number(listQueryType.size) : DEFAULT_SIZE,
          sort: listQueryType.sort ? toSortArray(listQueryType.sort) : DEFAULT_SORT,
        },
        q: listQueryType.q?.trim(),
        ageYear: listQueryType.ageYear ? Number(listQueryType.ageYear) : undefined,
        gender: listQueryType.gender ? (listQueryType.gender as Gender) : undefined,
        state: listQueryType.state ? listQueryType.state : undefined,
        city: listQueryType.city ? listQueryType.city : undefined,
        endedAtFrom: listQueryType.endedAtFrom ? listQueryType.endedAtFrom : undefined,
        endedAtTo: listQueryType.endedAtTo ? listQueryType.endedAtTo : undefined,
        testType: listQueryType.testType ? (listQueryType.testType as DataSearchTestType) : undefined,
        onlyCompleted: listQueryType.onlyCompleted === "true" ? true : undefined,
        excludeTestAccount: listQueryType.excludeTestAccount === "true" ? true : undefined,
      }).then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQueryType]);

  return {
    searchTestResultItemList,
    totalCnt,
    testResultItemList,
    pageCondition,
    getSearchParam,
  };
}
